export type NotificationPermissionState = NotificationPermission | 'unsupported';
export type NotificationDeliveryState =
  | 'unsupported'
  | 'default'
  | 'denied'
  | 'setup-required'
  | 'enabling'
  | 'enabled'
  | 'retryable-error';

export interface NotificationPermissionEnvironment {
  isSecureContext: boolean;
  notification?: {
    readonly permission: NotificationPermission;
    requestPermission(): Promise<NotificationPermission>;
  };
}

export interface PushSubscriptionLike {
  readonly endpoint: string;
  toJSON(): PushSubscriptionJSON;
  unsubscribe(): Promise<boolean>;
}

export interface PushManagerLike {
  getSubscription(): Promise<PushSubscriptionLike | null>;
  subscribe(options: PushSubscriptionOptionsInit): Promise<PushSubscriptionLike>;
}

export interface ServiceWorkerRegistrationLike {
  readonly pushManager: PushManagerLike;
}

export interface ServiceWorkerContainerLike {
  register(
    scriptURL: string | URL,
    options?: RegistrationOptions,
  ): Promise<ServiceWorkerRegistrationLike>;
}

export interface NotificationCapabilityEnvironment extends NotificationPermissionEnvironment {
  serviceWorker?: ServiceWorkerContainerLike;
  hasPushManager: boolean;
}

function getBrowserNotificationEnvironment(): NotificationCapabilityEnvironment {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { isSecureContext: false, hasPushManager: false };
  }

  return {
    isSecureContext: window.isSecureContext,
    notification: typeof window.Notification === 'undefined'
      ? undefined
      : window.Notification,
    serviceWorker: 'serviceWorker' in navigator
      ? navigator.serviceWorker as unknown as ServiceWorkerContainerLike
      : undefined,
    hasPushManager: typeof window.PushManager !== 'undefined',
  };
}

function isNotificationPermission(value: unknown): value is NotificationPermission {
  return value === 'default' || value === 'granted' || value === 'denied';
}

export function getNotificationPermissionState(
  environment: NotificationPermissionEnvironment = getBrowserNotificationEnvironment(),
): NotificationPermissionState {
  if (!environment.isSecureContext || !environment.notification) return 'unsupported';

  return isNotificationPermission(environment.notification.permission)
    ? environment.notification.permission
    : 'unsupported';
}

export function isNotificationDeliverySupported(
  vapidPublicKey: string,
  environment: NotificationCapabilityEnvironment = getBrowserNotificationEnvironment(),
): boolean {
  if (getNotificationPermissionState(environment) === 'unsupported'
    || !environment.serviceWorker
    || !environment.hasPushManager) {
    return false;
  }

  try {
    decodeVapidPublicKey(vapidPublicKey);
    return true;
  } catch {
    return false;
  }
}

export async function requestNotificationPermission(
  environment: NotificationPermissionEnvironment = getBrowserNotificationEnvironment(),
): Promise<NotificationPermissionState> {
  const currentState = getNotificationPermissionState(environment);
  if (currentState !== 'default') return currentState;

  try {
    const nextState = await environment.notification!.requestPermission();
    return isNotificationPermission(nextState)
      ? nextState
      : getNotificationPermissionState(environment);
  } catch {
    return getNotificationPermissionState(environment);
  }
}

export async function registerNotificationServiceWorker(
  vapidPublicKey: string,
  environment: NotificationCapabilityEnvironment = getBrowserNotificationEnvironment(),
): Promise<ServiceWorkerRegistrationLike | null> {
  if (!isNotificationDeliverySupported(vapidPublicKey, environment)) return null;
  return environment.serviceWorker!.register('/service-worker.js', { scope: '/' });
}

export async function getExistingPushSubscription(
  registration: ServiceWorkerRegistrationLike,
): Promise<PushSubscriptionLike | null> {
  return registration.pushManager.getSubscription();
}

export async function createPushSubscription(
  registration: ServiceWorkerRegistrationLike,
  vapidPublicKey: string,
): Promise<PushSubscriptionLike> {
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: decodeVapidPublicKey(vapidPublicKey),
  });
}

export async function deactivatePushSubscriptionForSignOut(
  registration: ServiceWorkerRegistrationLike | null,
  currentSubscription: PushSubscriptionLike | null,
  deactivateServer: (endpoint: string) => Promise<void>,
): Promise<void> {
  try {
    const subscription = currentSubscription
      ?? (registration ? await getExistingPushSubscription(registration) : null);
    if (!subscription) return;

    await Promise.allSettled([
      deactivateServer(subscription.endpoint),
      subscription.unsubscribe(),
    ]);
  } catch {
    // Notification cleanup must never block sign-out.
  }
}

export function decodeVapidPublicKey(
  value: string,
  decodeBase64: (encoded: string) => string = (encoded) => atob(encoded),
): Uint8Array<ArrayBuffer> {
  const normalized = value.trim().replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - normalized.length % 4) % 4);
  let decoded: string;
  try {
    decoded = decodeBase64(normalized + padding);
  } catch {
    throw new Error('The notification public key is invalid');
  }

  const bytes = Uint8Array.from(decoded, (character) => character.charCodeAt(0));
  if (bytes.length !== 65 || bytes[0] !== 4) {
    throw new Error('The notification public key is invalid');
  }
  return bytes;
}

export function getNotificationDeliveryState(options: {
  supported: boolean;
  permission: NotificationPermissionState;
  hasSubscription?: boolean;
  syncFailed?: boolean;
}): NotificationDeliveryState {
  if (!options.supported || options.permission === 'unsupported') return 'unsupported';
  if (options.permission === 'default') return 'default';
  if (options.permission === 'denied') return 'denied';
  if (options.syncFailed) return 'retryable-error';
  return options.hasSubscription ? 'enabled' : 'setup-required';
}

interface NotificationControlContent {
  buttonLabel: string;
  copy: string;
  disabled: boolean;
}

function getNotificationControlContent(
  state: NotificationDeliveryState,
): NotificationControlContent {
  if (state === 'enabled') {
    return {
      buttonLabel: 'Notifications Enabled',
      copy: 'This device can receive workout reminders and progress updates.',
      disabled: true,
    };
  }

  if (state === 'enabling') {
    return {
      buttonLabel: 'Enabling Notifications…',
      copy: 'Finishing notification setup for this device.',
      disabled: true,
    };
  }

  if (state === 'setup-required') {
    return {
      buttonLabel: 'Finish Enabling Notifications',
      copy: 'Permission is allowed, but this device still needs to finish notification setup.',
      disabled: false,
    };
  }

  if (state === 'retryable-error') {
    return {
      buttonLabel: 'Retry Notifications',
      copy: 'Notification setup did not finish. Your workout is unaffected, and you can try again.',
      disabled: false,
    };
  }

  if (state === 'denied') {
    return {
      buttonLabel: 'Notifications Blocked',
      copy: 'Notifications are blocked in your browser or device settings.',
      disabled: true,
    };
  }

  if (state === 'unsupported') {
    return {
      buttonLabel: 'Notifications Unavailable',
      copy: 'Notifications are not available in this browser. On iPhone or iPad, add Pass the Pushups to your Home Screen and open it there.',
      disabled: true,
    };
  }

  return {
    buttonLabel: 'Enable Notifications',
    copy: 'Allow this device to receive workout reminders and progress updates.',
    disabled: false,
  };
}

export function renderWorkoutNotificationControl(
  state: NotificationDeliveryState,
): string {
  const content = getNotificationControlContent(state);

  return `
    <section id="workout-notification-permission" class="notification-permission-box" aria-labelledby="workout-notification-heading">
      <h3 id="workout-notification-heading">Workout Notifications</h3>
      <p class="card-copy notification-permission-copy" aria-live="polite">${content.copy}</p>
      <button
        id="enable-notifications-btn"
        class="btn notification-permission-btn"
        type="button"${content.disabled ? ' disabled' : ''}
      >${content.buttonLabel}</button>
    </section>
  `;
}
